import { NavigationGuardNext, Route } from 'vue-router';

import store from '@/store';
import { readIsLoggedIn, readUserProfile } from '@/store/main/getters';
import { RoleId } from '@/lib/enum';
import clerk, { isClerkReady } from '@/lib/clerk';
import { regulationDatabaseUrl } from '@/env';
import { resolveLoginRedirectTarget } from '@/utils/redirectWhitelist';

export const ensureLoggedIn = async (
  from: Route,
  _: Route,
  next: NavigationGuardNext
) => {
  if (readIsLoggedIn(store)) {
    next();
  } else {
    const state =
      JSON.parse(sessionStorage.getItem('loginState') || '{}') || {};
    if (!state.logout && !state.tfaCompleted) {
      // Unless the user has explicitly logged out or completed 2FA,
      // add a redirect to the original page they were trying to access
      // so that they can return there after logging in.
      next({ name: 'login', query: { redirect: from.fullPath } });
    } else {
      next({ name: 'login' });
    }
  }
};

export const ensureLoggedOut = async (
  from: Route,
  __: Route,
  next: NavigationGuardNext
) => {
  if (readIsLoggedIn(store)) {
    // Redirect to the saved page if it was set, otherwise redirect to the dashboard
    const target = resolveLoginRedirectTarget(
      from.query.redirect,
      window.location.origin,
      regulationDatabaseUrl
    );
    if (target) {
      // A same-site path is handled by the SPA router; an absolute URL
      // (the regulation satellite app) needs a real browser navigation —
      // vue-router's next() cannot cross origins, especially in hash mode.
      if (target.startsWith('/')) {
        next(target);
      } else {
        window.location.href = target;
      }
      return;
    }
    next({ name: 'sdsUpload' });
  } else {
    next();
  }
};

export const ensureLoggedInAdmin = async (
  from: Route,
  to: Route,
  next: NavigationGuardNext
) => {
  const checkAdminAfterLogin = () => {
    const user = readUserProfile(store);
    const isAdminUser = user?.role_id === RoleId.USER_ADMIN.value;

    if (!isAdminUser) {
      next({ name: 'sdsList' });
      return;
    }
    next();
  };

  await ensureLoggedIn(from, to, (to) => {
    if (to === undefined) {
      checkAdminAfterLogin();
    } else {
      next(to);
    }
  });
};

export const ensureSignupEnabled = (
  _from: Route,
  _to: Route,
  next: NavigationGuardNext
) => {
  const { VUE_APP_ENV } = process.env;
  if (VUE_APP_ENV === 'development' || VUE_APP_ENV === 'e2e') {
    next();
  } else {
    next({ name: 'login' });
  }
};

export const ensureClerkAuthenticated = async (
  _from: Route,
  _to: Route,
  next: NavigationGuardNext
) => {
  // Wait for Clerk to be ready
  await isClerkReady;

  // Check if user has an active Clerk session
  if (clerk.session) {
    // Has Clerk session, allow access
    next();
  } else {
    // No Clerk session, redirect to login
    next({ name: 'login' });
  }
};
